import type { CanvasCard, CardType, DataTableCardData, PageLayout, SizePreset, TableCardConfig, TableRow } from "store/types";
import { DEFAULT_GRID_H, GRID_CONSTANTS, PRESET_GRID_W } from "store/types/reports/report-generator";

import {
  estimateRowH,
  getHeaderRowH,
  getTableTier,
  isTableCardWithData,
  measureVisibleHeight,
  TABLE_TIER_METRICS,
  TOGGLE_ICON_MIN_H
} from "./splitTablePages";

export interface LayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
}

export function cardToLayoutItem(card: CanvasCard): LayoutItem {
  return { i: card.id, x: card.gridX, y: card.gridY, w: card.gridW, h: card.gridH, minW: 2, minH: 1 };
}

export function layoutItemToCardUpdates(
  item: LayoutItem
): Pick<CanvasCard, "gridX" | "gridY" | "gridW" | "gridH" | "widthPercent" | "heightPx"> {
  return {
    gridX: item.x,
    gridY: item.y,
    gridW: item.w,
    gridH: item.h,
    widthPercent: gridWToWidthPercent(item.w),
    heightPx: item.h * GRID_CONSTANTS.GRID_CELL_HEIGHT
  };
}

export function cardsToLayout(cards: CanvasCard[]): LayoutItem[] {
  return cards.map(cardToLayoutItem);
}

export function gridWToWidthPercent(gridW: number): number {
  return Math.round((gridW / GRID_CONSTANTS.GRID_COLUMNS) * 100 * 100) / 100;
}

export function widthPercentToGridW(wp: number): number {
  return Math.max(1, Math.min(GRID_CONSTANTS.GRID_COLUMNS, Math.round((wp / 100) * GRID_CONSTANTS.GRID_COLUMNS)));
}

const FULL_WIDTH_CARD_TYPES: Set<CardType> = new Set(["table"]);

export function defaultGridDimensions(type: CardType): { gridW: number; gridH: number } {
  const gridW = FULL_WIDTH_CARD_TYPES.has(type) ? PRESET_GRID_W.F : PRESET_GRID_W.M;
  return { gridW, gridH: DEFAULT_GRID_H[type] };
}

export function findFirstAvailablePosition(
  existingItems: LayoutItem[],
  w: number,
  h: number,
  cols = GRID_CONSTANTS.GRID_COLUMNS,
  maxRows?: number
): { x: number; y: number } {
  const maxY = existingItems.reduce((max, item) => Math.max(max, item.y + item.h), 0);
  const scanHeight = maxY + h + 10;

  const occupied = new Set<string>();
  for (const item of existingItems) {
    for (let row = item.y; row < item.y + item.h; row++) {
      for (let col = item.x; col < item.x + item.w; col++) {
        occupied.add(`${col},${row}`);
      }
    }
  }

  for (let y = 0; y < scanHeight; y++) {
    if (maxRows != null && y + h > maxRows) break; // stop scanning past boundary
    for (let x = 0; x <= cols - w; x++) {
      let fits = true;
      for (let dy = 0; dy < h && fits; dy++) {
        for (let dx = 0; dx < w && fits; dx++) {
          if (occupied.has(`${x + dx},${y + dy}`)) fits = false;
        }
      }
      if (fits) return { x, y };
    }
  }

  return { x: 0, y: maxY };
}

export function applyPreset(
  card: CanvasCard,
  preset: SizePreset
): Pick<CanvasCard, "gridW" | "gridX" | "widthPercent" | "lastPreset"> {
  const gridW = PRESET_GRID_W[preset];
  // Try to keep the card centered around its current position
  const idealX = card.gridX + Math.floor((card.gridW - gridW) / 2);
  const gridX = Math.max(0, Math.min(idealX, GRID_CONSTANTS.GRID_COLUMNS - gridW));
  return {
    gridW,
    gridX,
    widthPercent: gridWToWidthPercent(gridW),
    lastPreset: preset
  };
}

export function migrateCardToGrid(card: CanvasCard): CanvasCard {
  if (card.gridW != null && card.gridW > 0) return card;

  const gridW = widthPercentToGridW(card.widthPercent);
  const gridH = DEFAULT_GRID_H[card.type] ?? 3;
  const calculatedX = card.orderInRow * widthPercentToGridW(card.widthPercent);
  const gridX = Math.max(0, Math.min(calculatedX, GRID_CONSTANTS.GRID_COLUMNS - gridW));
  const gridY = Math.max(0, card.rowIndex * gridH);

  return { ...card, gridX, gridY, gridW, gridH };
}

export function migrateCardsToGrid(cards: CanvasCard[]): CanvasCard[] {
  return cards.map(migrateCardToGrid);
}

const EMPTY_OFFSETS: Record<string, number> = {};

/**
 * Compute a content-driven gridH for a table card based on its actual data
 * (rows, columns, expand state). Returns the minimum gridH needed to display
 * all visible content without scrolling.
 *
 * `cardWidth` is the actual pixel width of the card's content area in the
 * current rendering context (edit mode grid layout or preview mode CSS layout).
 *
 * `minGridH` defaults to DEFAULT_GRID_H.table for original cards.
 * Pass a lower value (e.g. 1) for continuation cards that may have fewer rows.
 */
export function computeTableGridH(
  card: CanvasCard,
  expandedRows: Set<string>,
  cardWidth: number,
  minGridH?: number,
  indexOffsets?: Record<string, number>,
  extraOverheadPx?: number,
  isEditMode?: boolean
): number {
  const minH = minGridH ?? DEFAULT_GRID_H.table;

  if (!isTableCardWithData(card)) return minH;

  const tableData = card.data as DataTableCardData;
  const rawRows = tableData.data;
  // Move "Total" row to the end to match DataTableCard rendering order
  const totalIdx = rawRows.findIndex((r) => r.title?.toLowerCase() === "total");
  const tableRows =
    totalIdx > -1 && totalIdx < rawRows.length - 1
      ? [...rawRows.slice(0, totalIdx), ...rawRows.slice(totalIdx + 1), rawRows[totalIdx]]
      : rawRows;

  if (tableRows.length === 0) return minH;

  const tier = getTableTier(card.widthPercent);
  const { bodyFont, headerFont, padV, padH } = TABLE_TIER_METRICS[tier];
  const cardWidthPx = cardWidth;
  const colKeys = tableData.columns.map((col: string | { key: string }) => (typeof col === "string" ? col : col.key));
  const colTitles = tableData.columns.map((col: string | { key: string; title?: string }) =>
    typeof col === "string" ? col : col.title || col.key
  );
  const colCount = colKeys.length;
  const charWidth = bodyFont * 0.65;
  const singleLineH = Math.ceil(bodyFont * 1.5);

  // Use actual column widths when available for accurate wrapping estimation
  const storedWidths = (card.config as TableCardConfig).columnWidths;
  let colTextWidths: number | number[];
  if (storedWidths && colKeys.every((k) => storedWidths[k] != null)) {
    const rawPcts = colKeys.map((k) => storedWidths[k]);
    const sum = rawPcts.reduce((a, b) => a + b, 0);
    colTextWidths = rawPcts.map((pct) => Math.max((pct / sum) * cardWidthPx - 2 * padH, 10));
  } else {
    const colWidthPx = cardWidthPx / Math.max(colCount, 1);
    colTextWidths = Math.max(colWidthPx - 2 * padH, 10);
  }

  // In edit mode, parent rows render a toggle icon (18px) that exceeds text height
  // at S (14px) and M (17px) tiers. Pass the minimum height to estimateRowH.
  const parentIconMinH = isEditMode ? TOGGLE_ICON_MIN_H : 0;
  const rowHFn = (r: TableRow, depth: number) => estimateRowH(r, colKeys, colTextWidths, charWidth, singleLineH, padV, depth, parentIconMinH);
  const headerRowH = getHeaderRowH(headerFont, padV, colKeys, colTitles, colTextWidths);
  const tablePad = 8;

  const totalContentH = measureVisibleHeight(tableRows, expandedRows, "root", indexOffsets ?? EMPTY_OFFSETS, rowHFn);
  const totalPx = tablePad + headerRowH + totalContentH + CARD_BORDER_PX + (extraOverheadPx ?? 0);
  // A card spanning N grid rows has visual height: N * CELL_HEIGHT + (N-1) * GAP.
  // Solving for N: N = (totalPx + GAP) / (CELL_HEIGHT + GAP)
  const cellPlusGap = GRID_CONSTANTS.GRID_CELL_HEIGHT + GRID_CONSTANTS.GAP;
  const contentGridH = Math.ceil((totalPx + GRID_CONSTANTS.GAP) / cellPlusGap);

  return Math.max(minH, contentGridH);
}

const CARD_BORDER_PX = 2;

/** Height of the drag-handle bar rendered by CardWrapper in edit mode (non-readOnly cards). */
export const DRAG_HANDLE_HEIGHT = 28;

/**
 * Compute the actual pixel width of a grid item in the react-grid-layout canvas.
 * Matches RGL's internal `calcGridItemPosition` formula.
 */
export function gridItemWidth(pageWidth: number, gridW: number): number {
  const contentWidth = pageWidth - 56; // PageView padding: 28px each side
  const colWidth = (contentWidth - (GRID_CONSTANTS.GRID_COLUMNS - 1) * GRID_CONSTANTS.GAP) / GRID_CONSTANTS.GRID_COLUMNS;
  return colWidth * gridW + (gridW - 1) * GRID_CONSTANTS.GAP;
}

/** Preview mode card width (CSS grid layout with 48px padding each side). */
export function previewCardWidth(pageWidth: number, gridW: number): number {
  const contentWidth = pageWidth - 96; // PreviewMode padding: 48px each side
  const colWidth = (contentWidth - (GRID_CONSTANTS.GRID_COLUMNS - 1) * GRID_CONSTANTS.GAP) / GRID_CONSTANTS.GRID_COLUMNS;
  return colWidth * gridW + (gridW - 1) * GRID_CONSTANTS.GAP;
}

/**
 * Compute the maximum number of grid rows that fit within a page's content area.
 * Mirrors the formula in PageView — kept here so both the view and the context
 * can derive the same value without duplicating the math.
 */
export function computeMaxRows(pageHeight: number, pageLayout: PageLayout): number {
  const headerH = pageLayout === "header-content" ? 120 : pageLayout === "section" ? 150 : 0;
  const containerPadV = 16 * 2;
  const availableH = pageHeight - headerH - containerPadV - GRID_CONSTANTS.FOOTER_HEIGHT;
  const cellPlusGap = GRID_CONSTANTS.GRID_CELL_HEIGHT + GRID_CONSTANTS.GAP;
  return Math.floor((availableH + GRID_CONSTANTS.GAP) / cellPlusGap);
}

/**
 * Resolve overlaps by pushing items DOWN only (never pulls items up).
 * If `maxRowLimit` is provided, items are not pushed past that boundary —
 * any remaining overlap is left in place for the caller to handle.
 */
export function compactDownOnly(layout: readonly LayoutItem[], maxRowLimit?: number): LayoutItem[] {
  const sorted = [...layout].sort((a, b) => a.y - b.y || a.x - b.x);
  const resolved: LayoutItem[] = [];

  for (const item of sorted) {
    const clone = { ...item };
    let hasCollision = true;
    while (hasCollision) {
      hasCollision = false;
      for (const placed of resolved) {
        if (
          clone.x < placed.x + placed.w &&
          clone.x + clone.w > placed.x &&
          clone.y < placed.y + placed.h &&
          clone.y + clone.h > placed.y
        ) {
          const newY = placed.y + placed.h;
          if (maxRowLimit != null && newY + clone.h > maxRowLimit) {
            break; // can't push further — leave overlap for handler to reject
          }
          clone.y = newY;
          hasCollision = true;
          break;
        }
      }
    }
    resolved.push(clone);
  }

  return resolved;
}
